import type { Viewer } from 'cesium';
import {
    Cartesian3,
    SceneMode,
    BoundingSphere,
    HeadingPitchRange,
    Math as CesiumMath,
    Ray,
    Cartographic
} from 'cesium';

import Icons from './icons';
import './styles/compass.css';

import DomUtil from './DomUtil';
import Widget from './Widget';

interface CompassOptions {
    container?: Element;
    tips?: {
        inner?: string;
        outer?: string;
    };
}

class Compass extends Widget {
    private _outRing!: HTMLElement;

    constructor(viewer: Viewer, options: CompassOptions = {}) {
        const viewContainer = viewer.container.querySelector('.cesium-viewer')
        super(viewer, DomUtil.create('div', 'cesium-compass', options.container ?? viewContainer));
        this._wrapper.onclick = () => {
            this._handleClick();
        };
        this._wrapper.addEventListener('touchstart', () => {
            this._handleClick();
        }, { passive: true });
        this.enabled = true;
    }

    /**
     *
     * @private
     */
    protected _bindEvent() {
        this._viewer.scene.postRender.addEventListener(this._postRenderHandler, this);
    }

    /**
     *
     * @private
     */
    protected _unbindEvent() {
        this._viewer.scene.postRender.removeEventListener(this._postRenderHandler, this);
    }

    /**
     *
     * @private
     */
    private _postRenderHandler() {
        const heading = this._viewer.camera.heading;

        const innerSvg = this._outRing.children.item(0);
        if (innerSvg)
            (innerSvg as HTMLElement).style.cssText = `
    transform : rotate(-${heading}rad);
    -webkit-transform : rotate(-${heading}rad);
    `;
    }

    protected _mountContent() {
        this._outRing = DomUtil.parseDom(Icons.compass_outer, 'out-ring');
        this._wrapper.appendChild(this._outRing);

        this._ready = true;
    }



    /**
     *
     * @param event
     * @returns {boolean}
     * @private
     */
    private _handleClick() {

        const scene = this._viewer.scene;
        const camera = scene.camera;
        const sscc = scene.screenSpaceCameraController;
        if (scene.mode === SceneMode.MORPHING || !sscc.enableInputs) {
            return true;
        }
        if (scene.mode === SceneMode.COLUMBUS_VIEW && !sscc.enableTranslate) {
            return;
        }
        if (scene.mode === SceneMode.SCENE3D || scene.mode === SceneMode.COLUMBUS_VIEW) {
            if (!sscc.enableLook) {
                return;
            }
            if (scene.mode === SceneMode.SCENE3D) {
                if (!sscc.enableRotate) {
                    return;
                }
            }
        }
        const center = this._getCameraFocus(true);
        if (!center) {
            return;
        }
        const cameraPosition = scene.globe.ellipsoid.cartographicToCartesian(
            camera.positionCartographic,
        );
        const surfaceNormal = scene.globe.ellipsoid.geodeticSurfaceNormal(center);
        const focusBoundingSphere = new BoundingSphere(center, 0);
        camera.flyToBoundingSphere(focusBoundingSphere, {
            offset: new HeadingPitchRange(
                0,
                CesiumMath.PI_OVER_TWO - Cartesian3.angleBetween(surfaceNormal, camera.directionWC),
                Cartesian3.distance(cameraPosition, center),
            ),
            duration: 1.5,
        });
        return true;
    }

    /**
     *
     * @param inWorldCoordinates
     * @returns {Cartesian3|undefined}
     * @private
     */
    private _getCameraFocus(inWorldCoordinates: boolean) {
        let result: Cartesian3 | undefined = new Cartesian3();
        const scene = this._viewer.scene;
        const camera = scene.camera;
        if (scene.mode === SceneMode.MORPHING) {
            return undefined;
        }
        if (this._viewer.trackedEntity?.position) {
            result = this._viewer.trackedEntity.position.getValue(this._viewer.clock.currentTime);
        } else {
            const rayScratch = new Ray();
            rayScratch.origin = camera.positionWC;
            rayScratch.direction = camera.directionWC;
            result = scene.globe.pick(rayScratch, scene);
        }
        if (!result) {
            return undefined;
        }
        if (scene.mode === SceneMode.SCENE2D || scene.mode === SceneMode.COLUMBUS_VIEW) {
            result = camera.worldToCameraCoordinatesPoint(result);
            const unprojectedScratch = new Cartographic();
            if (inWorldCoordinates) {
                result = scene.globe.ellipsoid.cartographicToCartesian(
                    scene.mapProjection.unproject(result, unprojectedScratch),
                );
            }
        } else {
            if (!inWorldCoordinates) {
                result = camera.worldToCameraCoordinatesPoint(result);
            }
        }
        return result;
    }



}

export default Compass;